import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowLeft, Download, Calendar, MapPin, User, FileText, AlertCircle } from 'lucide-react';
import './DenunciaDetail.css';

const DenunciaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [denuncia, setDenuncia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const loadDenuncia = async () => {
      try {
        const docRef = doc(db, 'denuncias', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setDenuncia({ id: docSnap.id, ...docSnap.data() });
        } else {
          console.error('Denuncia no encontrada');
        }
      } catch (error) {
        console.error('Error loading denuncia:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDenuncia();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const docRef = doc(db, 'denuncias', id);
      await updateDoc(docRef, { status: newStatus });
      setDenuncia(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate();
      return format(date, "dd 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
    } catch {
      return 'N/A';
    }
  };

  const downloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!denuncia) {
    return (
      <div className="container">
        <div className="error-state card">
          <AlertCircle size={48} color="#DC2626" />
          <h2>Denuncia no encontrada</h2>
          <button onClick={() => navigate('/admin/dashboard')} className="btn btn-primary">
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container detail-container">
      <div className="detail-header">
        <button onClick={() => navigate('/admin/dashboard')} className="btn btn-secondary">
          <ArrowLeft size={20} />
          Volver
        </button>
        
        <div className="status-actions">
          <label>Estado:</label>
          <select 
            value={denuncia.status} 
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={updating}
            className="status-select"
          >
            <option value="Nueva">Nueva</option>
            <option value="En Revision">En Revisión</option>
            <option value="Cerrada">Cerrada</option>
          </select>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-card card">
          <div className="detail-title">
            <h1>Denuncia #{denuncia.id.substring(0, 8)}</h1>
            <div className="detail-meta">
              <span className="meta-item">
                <Calendar size={16} />
                {formatDate(denuncia.created_at)}
              </span>
            </div>
          </div>

          <section className="detail-section">
            <h2>Tipo de Delito</h2>
            <div className="delitos-tags">
              {denuncia.delitos.map((delito, index) => (
                <span key={index} className="delito-tag">{delito}</span>
              ))}
            </div>
            {denuncia.delito_otro_detalle && (
              <div className="otro-detalle">
                <strong>Especificación:</strong> {denuncia.delito_otro_detalle}
              </div>
            )}
          </section>

          <section className="detail-section">
            <h2>Descripción de los Hechos</h2>
            
            <div className="fact-item">
              <h3>¿Qué pasó?</h3>
              <p>{denuncia.detalle_hechos.que}</p>
            </div>

            <div className="fact-item">
              <h3>¿Cuándo pasó?</h3>
              <p>{denuncia.detalle_hechos.cuando}</p>
            </div>

            <div className="fact-item">
              <h3>¿Dónde pasó?</h3>
              <p>{denuncia.detalle_hechos.donde}</p>
            </div>

            <div className="fact-item">
              <h3>¿Cómo se detectó?</h3>
              <p>{denuncia.detalle_hechos.como}</p>
            </div>
          </section>

          <section className="detail-section">
            <h2>Involucrados</h2>
            
            <div className="involucrado-item">
              <User size={18} color="#F58220" />
              <div>
                <strong>Presunto Infractor:</strong>
                <p>{denuncia.involucrados.infractores}</p>
              </div>
            </div>

            {denuncia.involucrados.victimas && (
              <div className="involucrado-item">
                <User size={18} color="#DC2626" />
                <div>
                  <strong>Víctima/Afectado:</strong>
                  <p>{denuncia.involucrados.victimas}</p>
                </div>
              </div>
            )}

            {denuncia.involucrados.testigos && (
              <div className="involucrado-item">
                <User size={18} color="#666" />
                <div>
                  <strong>Posibles Testigos:</strong>
                  <p>{denuncia.involucrados.testigos}</p>
                </div>
              </div>
            )}
          </section>

          {denuncia.evidencias_urls && denuncia.evidencias_urls.length > 0 && (
            <section className="detail-section">
              <h2>Evidencias Adjuntas</h2>
              <div className="evidencias-list">
                {denuncia.evidencias_urls.map((url, index) => {
                  const filename = url.split('/').pop().split('?')[0];
                  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
                  
                  return (
                    <div key={index} className="evidencia-item">
                      {isImage ? (
                        <img src={url} alt={`Evidencia ${index + 1}`} className="evidencia-preview" />
                      ) : (
                        <div className="evidencia-file">
                          <FileText size={48} color="#F58220" />
                        </div>
                      )}
                      <button 
                        onClick={() => downloadFile(url, `evidencia_${index + 1}`)}
                        className="btn btn-secondary btn-download"
                      >
                        <Download size={18} />
                        Descargar
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="detail-section">
            <h2>Información del Denunciante</h2>
            {denuncia.denunciante?.es_anonimo ? (
              <div className="anonimo-notice">
                <AlertCircle size={20} />
                <span>Esta denuncia es anónima</span>
              </div>
            ) : (
              <div className="denunciante-info">
                {denuncia.denunciante?.nombre && (
                  <div className="info-item">
                    <strong>Nombre:</strong>
                    <span>{denuncia.denunciante.nombre}</span>
                  </div>
                )}
                {denuncia.denunciante?.email && (
                  <div className="info-item">
                    <strong>Email:</strong>
                    <span>{denuncia.denunciante.email}</span>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="detail-section">
            <div className="declaracion-notice">
              <p>✓ El denunciante ha aceptado la declaración de veracidad</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DenunciaDetail;
